import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAllCategories } from '../../hooks/useAllCategories';
import { useDragScroll } from '../../hooks/useDragScroll';

const ICON_COUNT = 11;
const CARD_TINTS = ['bg-champagne-dark', 'bg-damasco/30', 'bg-olivine/25', 'bg-esmeralda/20'];
const CARD_WIDTH_PX = 160;

export function CategoryGrid() {
  const { categories, isLoading, error, isEmpty } = useAllCategories();
  const {
    ref: scrollRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    onMouseEnter,
    onTouchStart,
    onTouchEnd,
    onClickCapture,
  } = useDragScroll<HTMLDivElement>();
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setHasOverflow(el.scrollWidth > el.clientWidth);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [categories, scrollRef]);

  if (isLoading || error || isEmpty) return null;

  const handleScrollPrev = () => {
    scrollRef.current?.scrollBy({ left: -CARD_WIDTH_PX * 3, behavior: 'smooth' });
  };

  const handleScrollNext = () => {
    scrollRef.current?.scrollBy({ left: CARD_WIDTH_PX * 3, behavior: 'smooth' });
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 pt-10">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-chocolate-dark mb-1">
        Compre por categoria
      </p>
      <div className="w-9 h-0.5 bg-chocolate mt-3 mb-6" />

      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-4 sm:gap-5 overflow-x-auto no-scrollbar pb-2 pr-4 cursor-grab select-none [scroll-behavior:auto]"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          onMouseEnter={onMouseEnter}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onClickCapture={onClickCapture}
        >
          {categories.map((category, index) => {
            const iconNumber = (index % ICON_COUNT) + 1;
            const tint = CARD_TINTS[index % CARD_TINTS.length];
            return (
              <Link
                key={category.id}
                to={`/catalogo?cat=${category.id}`}
                className={`w-36 sm:w-40 shrink-0 rounded-2xl flex flex-col items-center text-center gap-3 py-5 px-3 ${tint} hover:opacity-90 transition-opacity`}
              >
                <img
                  src={`/images/categories/icon-${iconNumber}.webp`}
                  alt=""
                  className="w-16 h-16 sm:w-20 sm:h-20"
                />
                <span className="font-semibold text-text-dark text-base">{category.name}</span>
              </Link>
            );
          })}
        </div>
        {hasOverflow && (
          <>
            <button
              type="button"
              onClick={handleScrollPrev}
              aria-label="Anterior"
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-10 h-10 rounded-full bg-white border border-champagne shadow-md items-center justify-center text-text-dark hover:bg-champagne transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleScrollNext}
              aria-label="Próximo"
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-10 h-10 rounded-full bg-white border border-champagne shadow-md items-center justify-center text-text-dark hover:bg-champagne transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
